import { useState } from "react";
import PropTypes from "prop-types";
import { Link } from "react-router-dom";
import Grid from "@mui/material/Grid";
import Alert from "@mui/material/Alert";
import Chip from "@mui/material/Chip";
import Icon from "@mui/material/Icon";
import Tooltip from "@mui/material/Tooltip";
import Switch from "@mui/material/Switch";
import FormControlLabel from "@mui/material/FormControlLabel";

import MDBox from "components/MDBox";
import MDButton from "components/MDButton";
import MDInput from "components/MDInput";
import MDTypography from "components/MDTypography";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import PageGuide from "components/admin/PageGuide";
import Footer from "examples/Footer";
import PageCard from "components/admin/PageCard";
import AdminTable from "components/admin/AdminTable";
import ActionDialog from "components/admin/ActionDialog";
import GameText from "components/admin/GameText";
import { useConfirm } from "components/admin/Feedback";
import { useFetch, useRunner } from "components/admin/hooks";
import api from "api/client";
import { useAuth } from "auth/AuthContext";
import { PERMISSIONS } from "auth/access";
import { formatDate, formatNumber } from "utils/format";
import SeasonPanel from "layouts/arena-event/SeasonPanel";

const PHASES = {
  idle: { label: "Chưa mở", color: "default" },
  register: { label: "Đang báo danh", color: "info" },
  fighting: { label: "Đang thi đấu", color: "success" },
};

/** Giây còn lại dạng mm:ss */
function clock(seconds) {
  const s = Math.max(0, seconds || 0);
  return `${String(Math.floor(s / 60)).padStart(2, "0")}:${String(s % 60).padStart(2, "0")}`;
}

function Info({ label, children }) {
  return (
    <MDBox display="flex" justifyContent="space-between" gap={2} py={0.5}>
      <MDTypography variant="button" color="text">
        {label}
      </MDTypography>
      <MDTypography variant="button" fontWeight="medium" textAlign="right">
        {children}
      </MDTypography>
    </MDBox>
  );
}

Info.propTypes = {
  label: PropTypes.node.isRequired,
  children: PropTypes.node.isRequired,
};

function ArenaEvent() {
  const canEdit = useAuth().can(PERMISSIONS.ARENA_EVENT_EDIT);
  const confirm = useConfirm();
  const [run, busy] = useRunner();
  const status = useFetch(() => api.get("/api/admin/arena-event"), [], 2000);
  const [openDialog, setOpenDialog] = useState(false);
  const [registerSeconds, setRegisterSeconds] = useState(120);
  const [matchMinutes, setMatchMinutes] = useState(1);
  const [stopDialog, setStopDialog] = useState(false);
  const [refund, setRefund] = useState(true);

  const data = status.data;
  const phase = PHASES[data?.phase] || PHASES.idle;

  const act = async (action, message) => {
    const res = await run(action, message);
    if (res.ok) status.reload(true);
    return res.ok;
  };

  const openNow = async () => {
    const ok = await act(
      () =>
        api.post("/api/admin/arena-event/open", {
          registerSeconds: Number(registerSeconds),
          matchMinutes: Number(matchMinutes),
        }),
      "Đã mở báo danh lôi đài"
    );
    if (ok) setOpenDialog(false);
  };

  const startNow = async () => {
    if (
      !(await confirm({
        title: "Bắt đầu thi đấu ngay",
        message:
          "Hết giờ báo danh ngay bây giờ. Ai không có mặt ở phòng chờ sẽ bị loại và không hoàn phí.",
        color: "warning",
        confirmText: "Bắt đầu",
      }))
    )
      return;
    act(() => api.post("/api/admin/arena-event/start"), "Lôi đài bắt đầu thi đấu");
  };

  const endRound = async () => {
    if (
      !(await confirm({
        title: "Kết thúc vòng ngay",
        message:
          "Các trận đang đánh chốt ngay như hết giờ (bên còn nhiều % máu hơn thắng), bỏ thời gian chờ còn lại của vòng. Vòng sau ghép khi mọi người đã về phòng chờ.",
        color: "warning",
        confirmText: "Kết thúc vòng",
      }))
    )
      return;
    act(() => api.post("/api/admin/arena-event/end-round"), "Đã kết thúc vòng");
  };

  const stopNow = async () => {
    const ok = await act(
      () => api.post("/api/admin/arena-event/stop", { refund }),
      (result) =>
        refund
          ? `Đã dừng lôi đài, hoàn phí ${result.refunded} người${
              result.notRefunded ? `, ${result.notRefunded} người offline chưa hoàn được` : ""
            }${
              result.eliminatedSkipped
                ? `, ${result.eliminatedSkipped} người đã bị loại không được hoàn`
                : ""
            }`
          : "Đã dừng lôi đài"
    );
    if (ok) setStopDialog(false);
  };

  const eliminate = async (row) => {
    if (
      !(await confirm({
        title: "Loại khỏi lôi đài",
        message: `Loại ${row.name} khỏi lôi đài? Trận đang đánh (nếu có) vẫn đánh nốt, sau đó không được ghép nữa, không hoàn phí.`,
        color: "error",
        confirmText: "Loại",
      }))
    )
      return;
    act(
      () => api.post("/api/admin/arena-event/eliminate", { userId: row.userId }),
      `Đã loại ${row.name}`
    );
  };

  const setPaused = async (paused) => {
    if (paused) {
      const yes = await confirm({
        title: "Tắt lịch tự mở lôi đài",
        message: `Máy chủ sẽ KHÔNG tự mở lôi đài vào các giờ ${data.openHours
          .map((h) => `${h}h`)
          .join(
            ", "
          )} cho tới khi bật lại. Phiên đang chạy (nếu có) vẫn chạy tiếp.\n\nKhởi động lại máy chủ thì lịch tự bật lại.`,
        color: "warning",
        confirmText: "Tắt lịch",
      });
      if (!yes) return;
    }
    act(
      () => api.put("/api/admin/arena-event/schedule", { paused }),
      paused ? "Đã tắt lịch tự mở" : "Đã bật lịch tự mở"
    );
  };

  const entrantStatus = (e) => {
    if (e.eliminated) return <Chip size="small" color="error" label="Bị loại" />;
    if (e.finished) return <Chip size="small" color="success" label="Đã xong" />;
    if (!e.online) return <Chip size="small" color="warning" label="Offline" />;
    if (e.inArena) return <Chip size="small" color="info" label="Đang ở sân" />;
    if (e.inLobby) return <Chip size="small" color="primary" label="Ở phòng chờ" />;
    return <Chip size="small" color="warning" label="Ngoài phòng chờ" />;
  };

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <MDBox py={3}>
        <PageGuide page="arena-event" />
        {status.error && (
          <Alert severity="error">Không lấy được trạng thái lôi đài: {status.error}</Alert>
        )}
        {data && !data.lobbyReady && (
          <Alert severity="error" sx={{ mt: 2 }}>
            Máy chủ thiếu map Phòng chờ lôi đài nên người chơi <b>không báo danh được</b>. Báo kỹ
            thuật cập nhật dữ liệu.
            <MDTypography variant="caption" display="block" sx={{ opacity: 0.7 }}>
              Cho kỹ thuật: chạy Database/arena_lobby_map.sql (tools/DbMigrate --run
              arena_lobby_map.sql) rồi khởi động lại GServer.
            </MDTypography>
          </Alert>
        )}
        <Grid container spacing={3}>
          <Grid item xs={12} lg={5}>
            <PageCard
              title="Trạng thái"
              subtitle="Tự làm mới mỗi 2 giây"
              color="warning"
              actions={
                <MDButton size="small" color="white" onClick={() => status.reload()}>
                  <Icon>refresh</Icon>
                </MDButton>
              }
            >
              {data && (
                <MDBox>
                  <MDBox display="flex" gap={1} flexWrap="wrap" mb={1}>
                    <Chip color={phase.color} label={phase.label} />
                    {data.openedByAdmin && <Chip color="secondary" label="Mở bằng tay (test)" />}
                    {data.schedulePaused && <Chip color="error" label="Lịch tự mở đang tắt" />}
                  </MDBox>
                  {data.phase === "register" && (
                    <Info label="Hết giờ báo danh sau">{clock(data.registerSecondsLeft)}</Info>
                  )}
                  {data.phase === "fighting" && (
                    <>
                      <Info label="Vòng">
                        {data.round}/{data.maxMatches}
                      </Info>
                      <Info label="Hết giờ thi đấu của vòng sau">
                        {clock(data.roundSecondsLeft)}
                      </Info>
                      <Info label="Ghép vòng sau sau">{clock(data.nextRoundSecondsLeft)}</Info>
                    </>
                  )}
                  <Info label="Người báo danh">{data.entrants.length}</Info>
                  <Info label="Lịch tự mở">
                    {data.openHours.map((h) => `${h}h`).join(", ")} ({data.registerMinutes} phút báo
                    danh)
                  </Info>
                  <Info label="Lần mở theo lịch tiếp theo">
                    {data.schedulePaused ? "Đang tắt" : formatDate(data.nextScheduledOpen)}
                  </Info>
                  <Info label="Thời gian một trận">
                    {data.matchMinutes} phút
                    {data.matchMinutes !== data.defaultMatchMinutes &&
                      ` (mặc định ${data.defaultMatchMinutes})`}
                  </Info>
                  <Info label="Một lượt / nghỉ giữa vòng">
                    {data.turnSeconds} giây / {data.roundBreakSeconds} giây
                  </Info>
                  <Info label="Số trận / cấp pet tối thiểu">
                    {data.maxMatches} trận / cấp {data.minPetLevel}
                  </Info>
                  <Info label="Điểm thắng / thua">
                    +{data.winPoints} / +{data.losePoints}
                  </Info>
                  <Info label="Phí">
                    {formatNumber(data.priceGold)} vàng hoặc {formatNumber(data.priceCoin)} ngọc
                  </Info>
                  <Info label="Ghép lực chiến sát nhau">
                    chênh tối đa {data.powerRangePercent}%
                  </Info>

                  {canEdit && (
                    <MDBox mt={2} display="flex" flexDirection="column" gap={1}>
                      <FormControlLabel
                        sx={{ ml: 0 }}
                        control={
                          <Switch
                            checked={!data.schedulePaused}
                            disabled={busy}
                            onChange={(e) => setPaused(!e.target.checked)}
                          />
                        }
                        label="Tự mở theo lịch"
                      />
                      <MDBox display="flex" gap={1} flexWrap="wrap">
                        <Tooltip
                          title={
                            data.manualOpenBlocked
                              ? "Còn dưới 5 phút tới giờ mở theo lịch, không mở tay được"
                              : "Mở báo danh ngay, chọn thời gian báo danh và thời gian trận ngắn để test"
                          }
                        >
                          <span>
                            <MDButton
                              variant="gradient"
                              color="info"
                              size="small"
                              disabled={busy || data.phase !== "idle" || data.manualOpenBlocked}
                              onClick={() => setOpenDialog(true)}
                            >
                              <Icon>play_arrow</Icon>&nbsp;Mở báo danh ngay
                            </MDButton>
                          </span>
                        </Tooltip>
                        <Tooltip title="Hết giờ báo danh ngay, vòng 1 bắt đầu">
                          <span>
                            <MDButton
                              variant="gradient"
                              color="success"
                              size="small"
                              disabled={busy || data.phase !== "register"}
                              onClick={startNow}
                            >
                              <Icon>sports_kabaddi</Icon>&nbsp;Bắt đầu thi đấu
                            </MDButton>
                          </span>
                        </Tooltip>
                        <Tooltip title="Chốt các trận đang đánh như hết giờ, bỏ thời gian chờ của vòng">
                          <span>
                            <MDButton
                              variant="gradient"
                              color="warning"
                              size="small"
                              disabled={busy || data.phase !== "fighting"}
                              onClick={endRound}
                            >
                              <Icon>skip_next</Icon>&nbsp;Kết thúc vòng
                            </MDButton>
                          </span>
                        </Tooltip>
                        <Tooltip title="Dừng hẳn lôi đài, có thể hoàn phí">
                          <span>
                            <MDButton
                              variant="gradient"
                              color="error"
                              size="small"
                              disabled={busy || data.phase === "idle"}
                              onClick={() => setStopDialog(true)}
                            >
                              <Icon>stop</Icon>&nbsp;Dừng lôi đài
                            </MDButton>
                          </span>
                        </Tooltip>
                      </MDBox>
                    </MDBox>
                  )}
                </MDBox>
              )}
            </PageCard>
          </Grid>
          <Grid item xs={12} lg={7}>
            <PageCard
              title="Trận đang đánh"
              subtitle={data?.phase === "fighting" ? `Vòng ${data.round}` : "Chưa thi đấu"}
              color="success"
              noPadding
            >
              <AdminTable
                loading={status.loading && !data}
                rows={data?.matches || []}
                rowKey={(m, i) => `${m.round}-${i}`}
                emptyText="Không có trận"
                columns={[
                  {
                    key: "one",
                    label: "Hai bên",
                    render: (m) => (
                      <MDBox>
                        {m.one} <MDTypography variant="caption">vs</MDTypography> {m.two}
                        <MDTypography variant="caption" color="text" display="block">
                          Lực chiến {formatNumber(m.onePower)} / {formatNumber(m.twoPower)}
                        </MDTypography>
                      </MDBox>
                    ),
                  },
                  {
                    key: "state",
                    label: "Trạng thái",
                    render: (m) =>
                      m.done ? (
                        <Chip
                          size="small"
                          color={m.returned ? "success" : "info"}
                          label={m.returned ? "Xong" : "Xong, đang về phòng chờ"}
                        />
                      ) : (
                        <Chip
                          size="small"
                          color="warning"
                          label={`Đang đánh ${clock(m.secondsLeft)}`}
                        />
                      ),
                  },
                  { key: "winner", label: "Thắng", render: (m) => m.winner || "-" },
                ]}
              />
            </PageCard>
          </Grid>
          <Grid item xs={12}>
            <PageCard
              title="Người báo danh"
              subtitle="Sắp theo điểm lôi đài rồi lực chiến"
              color="info"
              noPadding
            >
              <AdminTable
                loading={status.loading && !data}
                rows={data?.entrants || []}
                rowKey="userId"
                emptyText="Chưa có ai báo danh"
                columns={[
                  {
                    key: "name",
                    label: "Người chơi",
                    render: (e) => (
                      <MDBox>
                        <Link to={`/players/${e.userId}`}>{e.name}</Link>
                        {e.paid && (
                          <MDTypography variant="caption" color="text" display="block">
                            đã đóng phí {e.paid}
                          </MDTypography>
                        )}
                      </MDBox>
                    ),
                  },
                  {
                    key: "pet",
                    label: "Pet đã lưu",
                    render: (e) => (
                      <MDBox>
                        <GameText text={e.petName} /> (cấp {e.petLvl})
                        <MDTypography variant="caption" color="text" display="block">
                          Lực chiến {formatNumber(e.power)}
                        </MDTypography>
                      </MDBox>
                    ),
                  },
                  {
                    key: "record",
                    label: "Trận",
                    align: "center",
                    render: (e) =>
                      `${e.matches}/${data.maxMatches} trận · ${e.wins} thắng, ${
                        e.matches - e.wins
                      } thua${e.byeCount ? ` · ${e.byeCount} lượt thiếu đối thủ` : ""}`,
                  },
                  { key: "points", label: "Điểm", align: "center", render: (e) => `+${e.points}` },
                  { key: "status", label: "Trạng thái", render: entrantStatus },
                  ...(canEdit
                    ? [
                        {
                          key: "actions",
                          label: "",
                          align: "right",
                          render: (e) =>
                            !e.eliminated &&
                            !e.finished && (
                              <MDButton
                                size="small"
                                variant="text"
                                color="error"
                                disabled={busy}
                                onClick={() => eliminate(e)}
                              >
                                Loại
                              </MDButton>
                            ),
                        },
                      ]
                    : []),
                ]}
              />
            </PageCard>
          </Grid>
        </Grid>
        <MDBox mt={3}>
          <SeasonPanel canEdit={canEdit} />
        </MDBox>
      </MDBox>

      <ActionDialog
        open={openDialog}
        title="Mở báo danh ngay"
        onClose={() => setOpenDialog(false)}
        onSubmit={openNow}
        busy={busy}
        submitText="Mở báo danh"
        description="Mở một phiên lôi đài ngay để test. Toàn server được thông báo như phiên theo lịch, người báo danh vẫn mất phí. Hết phiên, thời gian trận trở về mặc định."
      >
        <MDBox display="flex" gap={0.5} flexWrap="wrap" alignItems="center">
          <MDTypography variant="caption" color="text" mr={0.5}>
            Báo danh trong:
          </MDTypography>
          {[60, 120, 300, 600].map((s) => (
            <Chip
              key={s}
              size="small"
              label={`${s / 60} phút`}
              color={Number(registerSeconds) === s ? "info" : "default"}
              variant={Number(registerSeconds) === s ? "filled" : "outlined"}
              onClick={() => setRegisterSeconds(s)}
            />
          ))}
        </MDBox>
        <MDInput
          fullWidth
          type="number"
          label="Thời gian báo danh (giây, 30 - 900)"
          value={registerSeconds}
          onChange={(e) => setRegisterSeconds(e.target.value)}
          inputProps={{ min: 30, max: 900 }}
        />
        <MDInput
          fullWidth
          type="number"
          label="Thời gian tối đa một trận (phút, 1 - 10)"
          value={matchMinutes}
          onChange={(e) => setMatchMinutes(e.target.value)}
          inputProps={{ min: 1, max: 10 }}
        />
        <MDTypography variant="caption" color="text">
          Cập nhật chỉ số pet chỉ được khi giờ báo danh còn hơn 1 phút, nên muốn test cả chức năng
          này thì để báo danh trên 60 giây.
        </MDTypography>
      </ActionDialog>

      <ActionDialog
        open={stopDialog}
        title="Dừng lôi đài"
        onClose={() => setStopDialog(false)}
        onSubmit={stopNow}
        busy={busy}
        color="error"
        submitText="Dừng"
        description={`${
          data ? `${data.entrants.length} người đang tham gia. ` : ""
        }Các trận đang đánh bị huỷ (không ai được điểm). Ai đã đánh ít nhất một trận nhận hộp tổng kết.`}
      >
        <FormControlLabel
          sx={{ ml: 0 }}
          control={<Switch checked={refund} onChange={(e) => setRefund(e.target.checked)} />}
          label="Hoàn phí báo danh (chỉ người đang online; đã thi đấu thì người bị loại không được hoàn)"
        />
      </ActionDialog>
      <Footer />
    </DashboardLayout>
  );
}

export default ArenaEvent;
